import { computed, defineComponent, h, nextTick, onBeforeUnmount, onMounted, ref, toRef, watchEffect } from 'vue';

import { classNames, addEventListenerWrapper } from '@components/utils';

import { useColumnWidth } from '../../composable';
import { useInjectTable } from '../../context';
import { ColGroup } from '../ColGroup';

import type { IHeaderProps } from '../Header/Header';
import type { NTable } from '../../interface';

export interface IFixedHolderProps<RecordType = NTable.TDefaultRecord> extends IHeaderProps<RecordType> {
  flattenColumns: readonly NTable.NColumn.IColumn<RecordType>[];
  noData: boolean;
  maxContentScroll: boolean;
  colWidths: readonly (string | number | undefined)[];
  columnCount: number;
  fixHeader?: boolean;
  stickyTopOffset?: number;
  stickyBottomOffset?: number;
  stickyClassName?: string;
  onScroll: (info: { currentTarget: HTMLDivElement; scrollLeft?: number }) => void;
}

export default defineComponent<IFixedHolderProps>({
  name: 'FixedHolder',
  inheritAttrs: false,
  props: [
    'columns',
    'flattenColumns',
    'stickyOffsets',
    'customHeaderRow',
    'noData',
    'maxContentScroll',
    'colWidths',
    'columnCount',
    'fixHeader',
    'stickyTopOffset',
    'stickyBottomOffset',
    'stickyClassName'
  ] as any,
  emits: ['scroll'],
  setup(props, { attrs, slots, emit }) {
    const tableContext = useInjectTable();
    const combinationScrollBarSize = computed(() =>
      tableContext.isSticky && !props.fixHeader ? 0 : tableContext.scrollbarSize
    );
    const scrollRef = ref();
    const wheelEvent = ref();

    const onWheel = (event: WheelEvent) => {
      const { currentTarget, deltaX } = event;

      if (deltaX) {
        emit('scroll', { currentTarget, scrollLeft: (currentTarget as any).scrollLeft + deltaX });
        event.preventDefault();
      }
    };

    onMounted(() => {
      nextTick(() => {
        wheelEvent.value = addEventListenerWrapper(scrollRef.value, 'wheel', onWheel);
      });
    });

    onBeforeUnmount(() => {
      wheelEvent.value?.remove();
    });

    // Check if all flattenColumns has specified width
    const allFlattenColumnsWithWidth = computed(() =>
      props.flattenColumns.every((column) => column.width && column.width !== 0 && column.width !== '0px')
    );

    const columnsWithScrollbar = ref<NTable.NColumn.TColumns<unknown>>([]);
    const flattenColumnsWithScrollbar = ref<NTable.NColumn.TColumns<unknown>>([]);

    watchEffect(() => {
      // Add scrollbar column
      const lastColumn = props.flattenColumns[props.flattenColumns.length - 1];
      const ScrollBarColumn: NTable.NColumn.IColumn<unknown> & { scrollbar: true } = {
        fixed: lastColumn ? lastColumn.fixed : null,
        scrollbar: true,
        customHeaderCell: () => ({
          class: 'cds-table__cell--scrollbar'
        })
      };

      columnsWithScrollbar.value = combinationScrollBarSize.value ? [...props.columns, ScrollBarColumn] : props.columns;
      flattenColumnsWithScrollbar.value = combinationScrollBarSize.value
        ? [...props.flattenColumns, ScrollBarColumn as NTable.NColumn.IColumn<unknown>]
        : (props.flattenColumns as NTable.NColumn.IColumn<unknown>[]);
    });

    // Calculate the sticky offsets
    const headerStickyOffsets = computed(() => {
      const { stickyOffsets } = props;

      return {
        ...stickyOffsets,
        right: [...stickyOffsets.right.map((width) => width + combinationScrollBarSize.value), 0],
        isSticky: tableContext.isSticky
      };
    });

    const mergedColumnWidth = useColumnWidth(toRef(props, 'colWidths'), toRef(props, 'columnCount'));

    return () => {
      const { noData, columnCount, stickyTopOffset, stickyBottomOffset, stickyClassName, maxContentScroll } = props;
      const { isSticky } = tableContext;

      return h(
        'div',
        {
          ref: scrollRef,
          class: classNames(attrs.class, {
            [stickyClassName as string]: !!stickyClassName
          }),
          style: {
            overflow: 'hidden',
            ...(isSticky ? { top: `${stickyTopOffset}px`, bottom: `${stickyBottomOffset}px` } : {})
          }
        },
        h(
          'table',
          {
            style: {
              tableLayout: 'fixed',
              visibility: noData || mergedColumnWidth.value ? null : 'hidden'
            }
          },
          [
            (!noData || !maxContentScroll || allFlattenColumnsWithWidth.value) &&
              h(ColGroup, {
                colWidths: mergedColumnWidth.value ? [...mergedColumnWidth.value, combinationScrollBarSize.value] : [],
                columnCount: columnCount + 1,
                columns: flattenColumnsWithScrollbar.value as any
              }),
            slots.default?.({
              ...props,
              stickyOffsets: headerStickyOffsets.value,
              columns: columnsWithScrollbar.value,
              flattenColumns: flattenColumnsWithScrollbar.value
            })
          ]
        )
      );
    };
  }
});
